<template>
    <v-container class="fill-height data-container" fluid>
        <v-stepper class="stepper" v-model="currentStep">
            <v-stepper-header style="height: 50px;">
                <v-stepper-step class="stepper-step" step="1" :complete="currentStep > 1" editable :color="'#c42111'" :rules="[() => validation.stepperForm($refs.userDetailsForm)]">User Details</v-stepper-step>
                <v-divider style="margin: 0px -6px;"></v-divider>
                <v-stepper-step class="stepper-step" step="2" :complete="currentStep > 2" editable :color="'#c42111'" :rules="[() => validation.stepperForm($refs.pagingSetupForm)]">Paging Setup</v-stepper-step>
                <v-divider style="margin: 0px -6px;"></v-divider>
                <v-stepper-step class="stepper-step" step="3" :complete="currentStep > 3" editable :color="'#c42111'" :rules="[() => validation.stepperForm($refs.pagingReductionForm)]">Paging Reduction</v-stepper-step>
                <v-divider style="margin: 0px -6px;"></v-divider>
                <v-stepper-step class="stepper-step" step="4" :complete="currentStep > 4" editable :color="'#c42111'" :rules="[() => validation.stepperForm($refs.permissionsForm)]">Permissions</v-stepper-step>
            </v-stepper-header>

            <v-stepper-items class="stepper-content-container">
                <v-stepper-content :step="1" class="stepper-content">
                    <v-form ref="userDetailsForm" :lazy-validation="true" v-model="formValid">
                        <div class="flex-container">
                            <div style="margin-right: 20px;">
                                <user-image :editUser="$props.user" :disabled="!(isCustomerAdmin || editingMyself) || !$props.user.user_id"></user-image>
                            </div>

                            <div style="flex-grow: 1;">
                                <v-row>
                                    <v-col cols="12" sm="6">
                                        <v-text-field label="Username" color="#c42111" v-model="$props.user.user_username" :rules="[validation.required($props.user.user_username, 'Username'), validation.noSpaces($props.user.user_username), validation.noBadChars($props.user, $props.user.user_username), $store.state.validationFuncs.noDuplicates($props.user.user_username, existingUsernamesList)]" :disabled="!isCustomerAdmin || editingSys || isEditingTelsasoftAdmin" />
                                    </v-col>
                                    <v-col cols="12" sm="6">
                                        <v-text-field label="Full Name" color="#c42111" v-model="$props.user.user_fullname" :rules="[validation.required($props.user.user_fullname, 'Full Name'), validation.noBadChars($props.user, $props.user.user_fullname)]" :disabled="!isCustomerAdmin || isEditingTelsasoftAdmin" />
                                    </v-col>
                                    <v-col cols="12" sm="6">
                                        <v-text-field autocomplete="off" label="Password" :type="showPassword ? 'text' : 'password'" color="#c42111" v-model="$props.user.user_password" @input="debounceCheckPasswordComplexity" :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'" @click:append="showPassword = !showPassword" :rules="[validation.pwStrength($props.user.user_password, passwordPass)]" :disabled="!(isCustomerAdmin || editingMyself)">
                                            <template v-slot:append-outer>
                                                <v-icon class="checking-password" v-if="checkingPassword">fas fa-spinner fa-spin</v-icon>
                                                <span class="password-strength" :class="passwordStrength" v-if="!checkingPassword && passwordStrength" v-text="passwordStrength"></span>
                                            </template>
                                        </v-text-field>
                                    </v-col>
                                    <v-col cols="12" sm="6">
                                        <v-text-field autocomplete="off" label="Confirm Password" :type="showPassword ? 'text' : 'password'" color="#c42111" v-model="confirmPassword" :rules="[validation.match($props.user.user_password, confirmPassword)]" :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'" @click:append="showPassword = !showPassword" :disabled="!(isCustomerAdmin || editingMyself)" />
                                    </v-col>
                                    <v-col cols="12" sm="6">
                                        <v-select label="Manager" v-model="$props.user.user_manager" color="#c42111" :items="managerList" :disabled="!isCustomerAdmin" />
                                    </v-col>

                                    <v-col cols="12" sm="3">
                                        <v-switch label="Account Locked" v-model="$props.user.user_locked" color="#c42111" :disabled="!isCustomerAdmin || editingSys || isEditingTelsasoftAdmin"></v-switch>
                                    </v-col>
                                    <v-col cols="12" sm="3">
                                        <v-switch v-model="$props.user.user_data.is_customer_admin" color="#c42111" :disabled="!isCustomerAdmin || isEditingSysUser || isEditingTelsasoftAdmin">
                                            <template v-slot:label>
                                                <span>
                                                    Is Admin
                                                    <v-tooltip bottom color="rgba(97, 97, 97, 1.0)">
                                                        <template v-slot:activator="{ on, attrs }">
                                                            <v-icon v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                                                        </template>
                                                        <span>Admin users will have full permissions, can edit other users, and create other admins.</span>
                                                    </v-tooltip>
                                                </span>
                                            </template>
                                        </v-switch>
                                    </v-col>

                                    <v-col cols="12" sm="6" v-if="editingMyself">
                                        <v-btn v-if="!secureDeviceRegistered" color="#2196F3" height="60" class="register-secure-device-button" @click="registerSecureDeviceClick"><v-icon large class="mr-3">mdi-security</v-icon> Register Secure Device</v-btn>
                                        <v-btn v-if="secureDeviceRegistered" color="green" height="60" class="register-secure-device-button"><v-icon large class="mr-3">mdi-check</v-icon> Secure Device Registered</v-btn>
                                    </v-col>
                                </v-row>
                            </div>
                        </div>
                    </v-form>
                </v-stepper-content>

                <v-stepper-content :step="2" class="stepper-content">
                <v-form ref="pagingSetupForm" :lazy-validation="true" v-model="formValid">
                    <v-row dense>
                        <v-col style="display: flex;">
                            <v-text-field style="display: inline-flex;" label="E-mail" color="#c42111" v-model="$props.user.user_page_email" :disabled="!pageEmailActive || !isCustomerAdmin" :rules="[validation.email($props.user.user_page_email)]" />
                            <div class="ml-2" style="display: inline-flex; align-items: center;" v-if="$props.user.user_page_email">
                                <v-tooltip bottom v-if="isEmailVerified">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-icon v-bind="attrs" v-on="on" color="#4caf50">mdi-check-circle</v-icon>
                                    </template>
                                    <span>Email address has been verified!</span>
                                </v-tooltip>

                                <v-tooltip bottom v-else-if="!isUserEditing">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-icon v-bind="attrs" v-on="on" color="#FB8C00">mdi-alert</v-icon>
                                    </template>
                                    <span>Email Address is not verified. Email paging functionality will be limited until this email adddress is verified by the user.</span>
                                </v-tooltip>

                                <v-btn color="#c42111" @click="verifyEmailClick" v-else :disabled="false">
                                    Verify Email Address
                                </v-btn>
                            </div>
                        </v-col>
                    </v-row>

                    <v-divider class="mt-2 mb-3" />

                    <v-row dense>
                        <v-col style="display: flex;">
                            <v-text-field style="display: inline-flex;" label="Phone" color="#c42111" v-model="$props.user.user_page_smpp" :disabled="!pageSmsActive || !isCustomerAdmin" />
                            <div class="ml-2" style="display: inline-flex; align-items: center;" v-if="$props.user.user_page_smpp">
                                <v-tooltip bottom v-if="isSmsVerified">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-icon v-bind="attrs" v-on="on" color="#4caf50">mdi-check-circle</v-icon>
                                    </template>
                                    <span>SMS number has been verified!</span>
                                </v-tooltip>

                                <v-tooltip bottom v-else-if="!isUserEditing">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-icon v-bind="attrs" v-on="on" color="#FB8C00">mdi-alert</v-icon>
                                    </template>
                                    <span>SMS number is not verified. SMS paging functionality will be limited until this number is verified by the user.</span>
                                </v-tooltip>

                                <v-btn color="#c42111" @click="verifyNumberClick" v-else :disabled="false">
                                    Verify Number
                                </v-btn>
                            </div>
                        </v-col>
                    </v-row>

                    <div>

                        <div class="mb-2" style="display: inline-block; margin-right: 30px;">
                            <v-card height="200" width="280" class="elevation-2" style="background-color: #303030;">
                                <v-card-title>
                                    <v-icon class="mr-2" color="#62F57A" large>mdi-android-messages</v-icon>
                                    <span class="mt-1" style="font-size: 24px;">SMS</span>
                                    <v-tooltip bottom v-if="$props.user.user_data.sms_enabled && !isSmsVerified">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-icon class="page-method-status" large v-bind="attrs" v-on="on" color="#FB8C00">mdi-alert</v-icon>
                                        </template>
                                        <span>SMS number is not verified. SMS paging functionality is <span style="color: #FB8C00; font-weight: bold;">limited.</span></span>
                                    </v-tooltip>
                                </v-card-title>
                                <v-divider />
                                <v-card-text>
                                    <v-switch label="Enabled" v-model="$props.user.user_data.sms_enabled" color="#c42111" :disabled="!isCustomerAdmin"></v-switch>
                                </v-card-text>
                            </v-card>
                        </div>

                        <div style="display: inline-block; min-width: calc(100% - 320px);">
                            <v-card class="elevation-4" style="border: black solid 1px;" :disabled="!hasRichNotificationLicense">
                                <div class="no-license" v-if="!hasRichNotificationLicense">
                                    <span class="no-license-text">Using Extended Rich Notifications requires a Rich Notifications license.</span>
                                </div>
                                <v-card-title>Rich Notifications Extensions</v-card-title>
                                <v-card-text>

                                    <span class="d-block mb-2">Rich Notifications Extensions are able to integreate your SMS number with the popular messaging services below.</span>

                                    <v-card height="200" width="280" class="float-left d-inline-block elevation-2 mr-2" style="background-color: #303030;">
                                        <v-card-title>
                                            <v-icon class="mr-1" color="#34A9DC" style="transform: rotate(-45deg);" large>mdi-send</v-icon>
                                            <span class="mt-1" style="font-size: 24px;">Telegram</span>
                                            <v-tooltip bottom v-if="$props.user.user_data.telegram_enabled && (!isSmsVerified || !$props.user.user_data.telegram_user_id)">
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-icon class="page-method-status" large v-bind="attrs" v-on="on" color="#ff5252">mdi-alert</v-icon>
                                                </template>
                                                <span v-if="!isSmsVerified">SMS number is not verified. Telegram paging functionality is <span style="color: #ff5252; font-weight: bold;">disabled.</span></span>
                                                <span v-else>Telegram account is not verified. Telegram paging functionality is <span style="color: #ff5252; font-weight: bold;">disabled.</span></span>
                                            </v-tooltip>
                                        </v-card-title>
                                        <v-divider />
                                        <v-card-text>
                                            <v-switch label="Enabled" v-model="$props.user.user_data.telegram_enabled" color="#c42111" :disabled="!isCustomerAdmin"></v-switch>
                                            <div v-if="isUserEditing && $props.user.user_data.telegram_enabled && !isSmsVerified && !$props.user.user_data.telegram_user_id">
                                                <span style="color: #FB8C00">Verify SMS number first.</span>
                                            </div>
                                            <div v-else-if="isUserEditing && $props.user.user_data.telegram_enabled && isSmsVerified && !$props.user.user_data.telegram_user_id">
                                                <v-btn color="#c42111" @click="setupTelegramIntegration">Setup Integration</v-btn>
                                            </div>
                                            <span v-else-if="$props.user.user_data.telegram_user_id" style="color: #4caf50;">Telegram ID: {{$props.user.user_data.telegram_user_id}}</span>
                                            <div v-else-if="$props.user.user_data.telegram_enabled" class="unverified-integration">
                                                <v-icon color="#FB8C00">mdi-alert</v-icon>
                                                <span>Unverified Integration</span>
                                            </div>
                                        </v-card-text>
                                    </v-card>

                                    <v-card height="200" width="280" class="d-inline-block elevation-2" style="background-color: #303030;">
                                        <v-card-title>
                                            <v-icon class="mr-2" color="#61D76B" large>mdi-whatsapp</v-icon>
                                            <span class="mt-1" style="font-size: 24px;">WhatsApp</span>
                                            <v-tooltip bottom v-if="$props.user.user_data.whatsapp_enabled && !isSmsVerified">
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-icon class="page-method-status" large v-bind="attrs" v-on="on" color="#ff5252">mdi-alert</v-icon>
                                                </template>
                                                <span>SMS number is not verified. WhatsApp paging functionality is <span style="color: #ff5252; font-weight: bold;">disabled.</span></span>
                                            </v-tooltip>
                                        </v-card-title>
                                        <v-divider />
                                        <v-card-text>
                                            <v-switch label="Enabled" v-model="$props.user.user_data.whatsapp_enabled" color="#c42111" :disabled="!isCustomerAdmin"></v-switch>
                                        </v-card-text>
                                    </v-card>

                                </v-card-text>
                            </v-card>

                        </div>
                    </div>

                    <v-divider class="mt-3 mb-2" />

                    <v-row dense>
                        <v-col>
                            <v-switch label="Page via push notifications" color="#c42111" v-model="$props.user.user_page_push_notif" :disabled="!isCustomerAdmin" />
                        </v-col>
                    </v-row>

                    <v-divider class="mt-1 mb-3" />

                    <v-row dense>
                        <v-col style="display: flex;">
                            <v-text-field style="display: inline-flex;" label="Microsoft Teams URL" color="#c42111" v-model="$props.user.user_page_teams_url" :disabled="!isCustomerAdmin" :rules="[$store.state.validationFuncs.validURL($props.user.user_page_teams_url)]" />
                            <div class="ml-2" style="display: inline-flex; align-items: center;" v-if="$props.user.user_page_teams_url">
                                <v-tooltip bottom v-if="isTeamsVerified">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-icon v-bind="attrs" v-on="on" color="#4caf50">mdi-check-circle</v-icon>
                                    </template>
                                    <span>Microsoft Teams URL has been verified!</span>
                                </v-tooltip>

                                <v-tooltip bottom v-else-if="!isUserEditing">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-icon v-bind="attrs" v-on="on" color="#FB8C00">mdi-alert</v-icon>
                                    </template>
                                    <span>Microsoft Teams URL is not verified. Teams paging functionality will be limited until this Teams URL is verified by the user.</span>
                                </v-tooltip>

                                <v-btn color="#c42111" @click="verifyTeamsClick" v-else :disabled="false">
                                    Verify Microsoft Teams URL
                                </v-btn>
                            </div>
                        </v-col>
                    </v-row>
                </v-form>
                </v-stepper-content>

                <v-stepper-content :step="3" class="stepper-content">
                    <v-form ref="pagingReductionForm" :lazy-validation="true" v-model="formValid">
                        <v-list>
                            <v-list-item class="paging-reducttion-item" v-for="method in [['Email', 'email'], ['SMS', 'sms'], ['Whats App', 'whatsapp'], ['Telegram', 'telegram'], ['Microsoft Teams', 'teams'], ['Audio', 'audio'], ['Client Notification', 'client_notification'], ['Push Notification', 'push_notification']]">
                                <v-list-item-content>
                                    <v-list-item-title>{{method[0]}}</v-list-item-title>
                                    <v-switch class="paging-reduction-setting" label="Enabled" color="#c42111" v-model="$props.user.paging_reduction_settings[`${method[1]}_enabled`]" :disabled="!isCustomerAdmin" />
                                    <v-switch class="paging-reduction-setting" label="Per Site" color="#c42111" v-model="$props.user.paging_reduction_settings[`${method[1]}_site_specific`]" :disabled="!isCustomerAdmin || !$props.user.paging_reduction_settings[`${method[1]}_enabled`]" />
                                    <v-text-field outlined dense class="paging-reduction-setting" label="Threshold" color="#c42111" type="number" min="2" max="1000" v-model="$props.user.paging_reduction_settings[`${method[1]}_threshold`]" :disabled="!isCustomerAdmin || !$props.user.paging_reduction_settings[`${method[1]}_enabled`]" :rules="[$store.state.validationFuncs.intRange($props.user.paging_reduction_settings[`${method[1]}_threshold`], 2, 1000, 'Threshold')]" />
                                    <v-text-field outlined dense class="paging-reduction-setting" label="Interval" suffix="sec" color="#c42111" type="number" min="30" max="86400" v-model="$props.user.paging_reduction_settings[`${method[1]}_interval`]" :disabled="!isCustomerAdmin || !$props.user.paging_reduction_settings[`${method[1]}_enabled`]" :rules="[$store.state.validationFuncs.intRange($props.user.paging_reduction_settings[`${method[1]}_interval`], 30, 86400, 'Interval')]" />
                                </v-list-item-content>
                            </v-list-item>
                        </v-list>
                    </v-form>
                </v-stepper-content>

                <v-stepper-content :step="4" class="stepper-content">
                    <v-form ref="permissionsForm" :lazy-validation="true" v-model="formValid">
                        <permissions :type="'user'" :id="$props.user.user_id" :permissionsList="$props.userResources.permissionsList" :activePermissions="user.permissions" :lockSave="lockSave" :disabled="!isCustomerAdmin" v-if="!$props.user.user_data.is_customer_admin"></permissions>
                        <span class="admin-perms-message" v-else>
                            This is user is an administrator. They will have full permissions.
                        </span>
                    </v-form>
                </v-stepper-content>
            </v-stepper-items>
        </v-stepper>

        <v-divider></v-divider>

        <v-row class="mt-3">
            <v-btn class="ml-3 mr-3" color="#c42111" @click="saveClick" :disabled="$props.lockSave || !(isCustomerAdmin || editingMyself)">Save</v-btn>
            <v-btn class="mr-3" color="#c42111" @click="cancelClicked">Cancel</v-btn>
        </v-row>

        <v-dialog v-model="showVerifyEmail" max-width="80%" width="350px" persistent>
            <verify-email v-if="showVerifyEmail" :editUser="$props.user" @cancel="showVerifyEmail = false" @success="setEmailVerified"></verify-email>
        </v-dialog>

        <v-dialog v-model="showVerifySMS" max-width="80%" width="350px" persistent>
            <verify-sms v-if="showVerifySMS" :editUser="$props.user" @cancel="showVerifySMS = false" @success="setNumberVerified"></verify-sms>
        </v-dialog>

        <v-dialog v-model="showVerifyTelegram" max-width="80%" width="550px" persistent>
            <verify-telegram v-if="showVerifyTelegram" :editUser="$props.user" @cancel="showVerifyTelegram = false" @success="setTelegramVerified"></verify-telegram>
        </v-dialog>

        <v-dialog v-model="showVerifyTeams" max-width="80%" width="350px" persistent>
            <verify-teams v-if="showVerifyTeams" :editUser="$props.user" @cancel="showVerifyTeams = false" @success="setTeamsVerified"></verify-teams>
        </v-dialog>
        
    </v-container>
</template>

<script>
import _ from 'lodash';
import Permissions from "@/components/Users/Permissions";
import UserImage from "@/components/Users/UserImage";
import VerifySms from "@/components/Users/VerifySms";
import VerifyEmail from "@/components/Users/VerifyEmail";
import VerifyTelegram from "@/components/Users/VerifyTelegram";
import VerifyTeams from "@/components/Users/VerifyTeams";
export default {
    name: 'UserEdit',
    props: ['user', 'userResources', 'lockSave'],
    components: {
        Permissions,
        UserImage,
        VerifySms,
        VerifyEmail,
        VerifyTelegram,
        VerifyTeams
    },
    data: () => ({
        formValid: true,
        currentStep: 1,
        confirmPassword: '',
        pageEmailActive: true,
        pageSmsActive: true,
        managerList: [],
        validation: {
            required: (value, fieldName) => ((value || value === 0) ? true : false) || `${fieldName} is required`,
            email: (value) => ((/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,4})+$/.test(value) || !value) || "Invalid email address"),
            match: (value, valueToMatch) => (value === valueToMatch) || `Password doesn't match`,
            noSpaces: (value) => (!(/\s/.test(value)) || 'Cannot contain spaces'),
            noBadChars: (user, value) => (user.user_data.is_telsasoft_admin || (!(/.*[\\\[\]].*/.test(value))) || 'Invalid characters'),
            pwStrength: (value, passwordPass) => ((!value || passwordPass) || "Password doesn't meet the complexity requirements"),
            stepperForm: (form) => ((form === undefined || form.validate()) || false),
        },
        editingMyself: false,
        editingSys: false,
        originalUsername: '',
        showPassword: false,
        checkingPassword: false,
        passwordStrength: '',
        passwordPass: false,
        showVerifySMS: false,
        showVerifyEmail: false,
        showVerifyTelegram: false,
        showVerifyTeams: false,
        secureDeviceRegistered: false,
    }),

    computed: {
        isCustomerAdmin: function() {
            return (this.$store.state.user.user_data.is_customer_admin);
        },

        isEditingSysUser: function() {
            return (this.$props.user.user_username == 'sys');
        },

        isEditingTelsasoftAdmin: function() {
            return (this.$props.user.user_data.is_telsasoft_admin);
        },

        isUserEditing: function() {
            return (this.$store.state.user.user_id == this.$props.user.user_id);
        },

        isSmsVerified: function() {
            return (this.$props.user.user_data && this.$props.user.user_data.sms_verified);
        },

        isEmailVerified: function() {
            return (this.$props.user.user_data && this.$props.user.user_data.email_verified);
        },

        isTeamsVerified: function() {
            return (this.$props.user.user_data && this.$props.user.user_data.teams_verified);
        },

        hasRichNotificationLicense: function() {
            return this.$store.state.batchConfig.telsasoft_cloud_sms;
        },

        existingUsernamesList() {
            let batchUsernames = this.$store.state.batchConfig.users.filter(u => u.user_id != this.$props.user.user_id).map(u => u.user_username);
            let resourceUsernames = this.$props.userResources.usersList.filter(u => u.user_id != this.$props.user.user_id).map(u => u.user_username);
            let combinedList = [...new Set([...batchUsernames, ...resourceUsernames])];
            return combinedList;
        }
    },

    methods: {
        createResourceLists: function() {
            this.managerList.push({text: '[none]', value: -1});
            for(let user of this.$props.userResources.usersList) {
                this.managerList.push({
                    text: user.user_fullname,
                    value: user.user_id
                });
            }
        },

        saveClick: function() {
            if(this.$refs.userDetailsForm.validate() && this.$refs.pagingSetupForm.validate() && this.$refs.pagingReductionForm.validate() && this.$refs.permissionsForm.validate()) {
                if(this.editingMyself && (this.$props.user.user_password || (this.originalUsername != this.$props.user.user_username))) { // User has changed their own password.
                    this.$store.commit('showConfirmModal', {
                        title: 'Are you sure?',
                        message: `You have modified your account password or username. This will forcibly log you off(all active devices) and you will be required to log back in with your new password and/or username. This will also unregister any secure device authenticators associated with this account. Are you sure you want to do this?`,
                        type: 'warning',
                        confirmAction: () => {
                            this.$emit('save', this.$props.user, true);
                            this.removeAuthenticatorCredentialsFromLocalStorage(this.$props.user.user_id);
                            this.$socket.emit('pw_kickoff');
                        }
                    });
                }
                else if(this.$props.user.user_id && (this.$props.user.user_password || (this.originalUsername != this.$props.user.user_username) || this.$props.user.user_locked)) { // Admin has modified another user's password or username, or has locked a user account.
                    // Check if the user being modified is logged in right now.
                    let matchingUserId = this.$props.user.user_id;
                    let matches = this.$store.state.socket.activeClients.filter(activeClient => {
                        return activeClient.user_id == matchingUserId;
                    });
                    if(matches.length) {
                        this.$store.commit('showConfirmModal', {
                            title: 'Are you sure?',
                            message: this.$props.user.user_locked ? `You have locked this user's account. There is currently <b>${matches.length}</b> instance(s) of this user logged in.
                                This will forcibly log off the user. Are you sure you want to do this?` : `You have modified this user's password or username. There is currently <b>${matches.length}</b> instance(s) of this user logged in.
                                This will forcibly log off the user and require them to log back in with the new username and/or password. Are you sure you want to do this?`,
                            type: 'warning',
                            confirmAction: () => {
                                this.$emit('save', this.$props.user);
                                this.removeAuthenticatorCredentialsFromLocalStorage(this.$props.user.user_id);
                                for(let match of matches) {
                                    this.$socket.emit('kickoff', match.sid);
                                }
                            }
                        });
                    }
                    else {
                        this.$emit('save', this.$props.user);
                        this.removeAuthenticatorCredentialsFromLocalStorage(this.$props.user.user_id);
                    }
                }
                else {
                    this.$emit('save', this.$props.user);
                }
            }
        },

        cancelClicked: function() {
            if(this.$store.state.isDirty) {
                this.$store.commit('showConfirmModal', {
                    title: 'Are you sure?',
                    message: 'Are you sure you want to abandon your changes without saving?',
                    type: 'warning',
                    confirmAction: () => {this.$store.commit('setIsDirty', false); this.$emit('cancel');}
                });
            }
            else {
                this.$emit('cancel');
            }
        },

        checkPasswordComplexity: function() {
            let route = (TELSA.apiHost + "auth/passwordcomplexity");
            this.checkingPassword = true;
            this.passwordStrength = "checking...";
            return this.$http.post(route, {args: {password: this.$props.user.user_password}}).then(response => {
                let result = response.body;
                console.log(result);
                this.checkingPassword = false;
                this.passwordStrength = result.strength;
                this.passwordPass = result.pass;
            });
        },

        verifyNumberClick: function() {
            this.showVerifySMS = true;
        },

        verifyEmailClick: function() {
            this.showVerifyEmail = true;
        },

        verifyTeamsClick: function() {
            this.showVerifyTeams = true;
        },

        setNumberVerified: function() {
            this.$props.user.user_data = this.$props.user.user_data || {};
            this.$props.user.user_data.sms_verified = true;
            this.showVerifySMS = false;
        },

        setEmailVerified: function() {
            this.$props.user.user_data = this.$props.user.user_data || {};
            this.$props.user.user_data.email_verified = true;
            this.showVerifyEmail = false;
        },

        setTelegramVerified: function(telegram_user_id) {
            this.$props.user.user_data = this.$props.user.user_data || {};
            this.$props.user.user_data.telegram_user_id = telegram_user_id;
            this.showVerifyTelegram = false;
        },

        setTeamsVerified: function() {
            this.$props.user.user_data = this.$props.user.user_data || {};
            this.$props.user.user_data.teams_verified = true;
            this.showVerifyTeams = false;
        },

        goToTutorials: function() {
            this.$store.commit('setTutorialsShortcut', 'Rich Notifications');
            this.$router.push('tutorials');
        },

        setupTelegramIntegration: function() {
            this.showVerifyTelegram = true;
        },

        debounceCheckPasswordComplexity: _.debounce(function() {
            this.checkPasswordComplexity();
        }, 500),

        registerSecureDeviceClick: function() {
            if(!window.PublicKeyCredential) {
                console.log('DEVICE DOES NOT SUPPORT PUBLIC KEY CREDENTIAL');
                return;
            }

            TELSA.loading(true);
            let route = `${TELSA.apiHost}auth/registerdevice`;
            return this.$http.get(route).then(response => {
                let result = response.body;
                let registration_options = result.registration_options;
                let challenge_id = result.challenge_id;
                registration_options.challenge = Uint8Array.from(registration_options.challenge, c => c.charCodeAt(0)).buffer; // The challenege needs to be an ArrayBuffer
                registration_options.user.id = Uint8Array.from(registration_options.user.id, c => c.charCodeAt(0));
                navigator.credentials.create({'publicKey': registration_options}).then(credentialAttestationResp => {
                    let credentialAttestationObj = {
                        authenticatorAttachment: credentialAttestationResp.authenticatorAttachment,
                        id: credentialAttestationResp.id,
                        rawId: btoa(String.fromCharCode(...new Uint8Array(credentialAttestationResp.rawId))),
                        response: {
                            attestationObject: btoa(String.fromCharCode(...new Uint8Array(credentialAttestationResp.response.attestationObject))),
                            clientDataJSON: btoa(String.fromCharCode(...new Uint8Array(credentialAttestationResp.response.clientDataJSON)))
                        },
                        type: credentialAttestationResp.type
                    };
                    return this.$http.post(route, {args: {challenge_id: challenge_id, credentialAttestationObj: credentialAttestationObj}}).then(credentialAttestationServerRegistrationResp => {
                        let authenticator_credentials = localStorage.getItem('authenticator_credentials');

                        if(authenticator_credentials && Array.isArray(JSON.parse(authenticator_credentials))) {
                            authenticator_credentials = JSON.parse(authenticator_credentials);
                            // If theres already 1 and only 1 and its this user, then we need to replace it. 
                            if(authenticator_credentials.length === 1 && authenticator_credentials[0].user_id == this.$store.state.user.user_id) {
                                // remove it now and we will re-add the new one next couple lines.
                                authenticator_credentials = [];
                            }
                        }
                        else {
                            authenticator_credentials = [];
                        }
                        authenticator_credentials.push({user_id: this.$store.state.user.user_id, authenticator_credential_id: TELSA.buf2hex(credentialAttestationResp.rawId)});
                        localStorage.setItem('authenticator_credentials', JSON.stringify(authenticator_credentials));
                        this.secureDeviceRegistered = true;
                        TELSA.loading(false);
                    });
                });
            });
        },

        removeAuthenticatorCredentialsFromLocalStorage: function(userId) {
            let authenticator_credentials = localStorage.getItem('authenticator_credentials');
            if(authenticator_credentials && Array.isArray(JSON.parse(authenticator_credentials))) {
                let new_authenticator_credentials = [];
                authenticator_credentials = JSON.parse(authenticator_credentials);
                new_authenticator_credentials = authenticator_credentials.filter(ac => ac.user_id != userId);
                localStorage.setItem('authenticator_credentials', JSON.stringify(new_authenticator_credentials));
            }
        }
    },

    watch: {
        user: {
            handler(newVal, oldVal) {
                if(_.isEqual((delete JSON.parse(JSON.stringify(oldVal)).user_data), (delete JSON.parse(JSON.stringify(newVal)).user_data))) {
                    return;
                }
                if(!this.$props.lockSave) {
                    this.$store.commit('setIsDirty', true);
                }
            },
            deep: true
        }
    },

    mounted() {
        this.createResourceLists();
        this.editingMyself = (this.$props.user.user_id == this.$store.state.user.user_id);
        this.editingSys = (this.$props.user.user_username == 'sys');
        this.originalUsername = this.$props.user.user_username;
        if(this.$props.user.user_data.sms_enabled == undefined) {
            this.$props.user.user_data.sms_enabled = !!this.$props.user.user_page_smpp;
        }
        if(this.$store.state.userPageVerifyShortcut) {
            this.currentStep = 2;
            this.$store.commit('setUserPageVerifyShortcut', false);
        }
    }

  
};
</script>

<style scoped>

    .stepper {
        background: #424242; 
        height: calc(100vh - 128px);
    }
    .stepper .stepper-step {
        padding: 12px 24px;
    }
    .stepper-content-container {
        overflow-y: auto;
        height: calc(100% - 55px);
    }
    .stepper-content {
        /* padding: 16px 4px 4px 4px; */
    }

    .password-strength {
        color: #ffffff;
        margin-top: 0.5em;
    }
    .password-strength.bad {
        color: red;
    }
    .password-strength.weak {
        color: orange;
    }
    .password-strength.good {
        color: teal;
    }
    .password-strength.strong {
        color: green;
    }

    .flex-container {
        flex-grow: 1;
        display: flex;
        width: 100%;
    }
    @media (max-width: 959px) {
        .flex-container {
            flex-grow: 1;
            display: block;
        }
    }

    .white-link {
        color: #ffffff;
        font-weight: bold;
        text-decoration: underline;
        cursor: pointer;
        text-transform: uppercase;
    }

    .unverified-integration {
        color: #FB8C00;
        font-weight: bold;
        text-transform: uppercase;
        margin-left: 0px;
    }
    .unverified-integration span {
        margin-left: 5px;
        vertical-align: bottom;
    }

    .page-method-status {
        position: absolute;
        right: 20px;
    }

    .no-license {
        position: absolute;
        z-index: 10;
        opacity: 0.8;
        text-align: center;
        background-color: #C42111;
        border: #000 solid 1px;
        padding: 15px 20px;
        border-radius: 10px !important;
        left: calc(50% - (618px / 2));
        top: calc(50% - (58px / 2));
    }

    .no-license .no-license-text {
        font-weight: bold;
        font-size: 18px;
    }

    .register-secure-device-button {
        font-size: 17px;
    }

    .paging-reduction-setting {
        margin-right: 10px;
        width: 120px;
        max-width: 120px;
    }

    .admin-perms-message {
        display: block;
        text-align: center;
        font-size: 24px;
        margin-top: calc(50vh - 180px);
    }

</style>
